import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';

/**
 * Reusable search and filter component
 * 
 * @param {Object} props
 * @param {string} props.searchQuery - Current search query
 * @param {Function} props.onSearchChange - Search change handler
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {Array} props.filters - Filter options: [{ value, label }]
 * @param {string} props.selectedFilter - Currently selected filter value
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {Function} props.onClear - Clear all filters handler
 */
export const SearchFilter = ({
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters = [],
  selectedFilter = 'all',
  onFilterChange,
  onClear,
  className = ''
}) => {
  return (
    <div className={`flex flex-col sm:flex-row gap-4 ${className}`}>
      {/* Search Input */}
      <div className="flex-1 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-10"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter Dropdown */}
      {filters.length > 0 && (
        <select
          value={selectedFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {filters.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </select>
      )}

      {/* Clear Button */}
      {(searchQuery || selectedFilter !== 'all') && onClear && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
        >
          Clear
        </Button>
      )}
    </div>
  );
};

/**
 * Simple type filter for room/car types
 */
export const TypeFilter = ({
  types = [],
  selectedType,
  onTypeChange,
  label = 'Filter by type:',
  allLabel = 'All Types',
  className = ''
}) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <label className="text-sm font-medium text-gray-600 whitespace-nowrap">
        {label}
      </label>
      <select
        value={selectedType}
        onChange={(e) => onTypeChange(e.target.value)}
        className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="all">{allLabel}</option>
        {types.map((type) => (
          <option key={type.id} value={type.id}>
            {type.name}
            {type.count !== undefined && ` (${type.count})`}
          </option>
        ))}
      </select>
    </div>
  );
};